import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

interface Props {
  /** What renders on the right of the header (e.g. mode toggle, sign out). */
  right?: ReactNode;
  onHome: () => void;
  /** Search input rendered between Home and the right slot. */
  searchSlot: ReactNode;
}

/**
 * Sticky top header — Home / Search / right-slot.
 *
 * - The Back button was removed in Phase 10 polish — bartenders use Home or
 *   tray-slot taps to navigate. The active drink is preserved either way.
 * - The online/offline + "synced N ago" indicator was extracted into its
 *   own `SyncIndicator` component (also exported from this file) so the
 *   parent can place it where it makes sense — e.g. right under the
 *   "Station · Travis" eyebrow line instead of buried under the search bar.
 */
export default function StickyHeader({ right, onHome, searchSlot }: Props) {
  return (
    <header style={styles.wrap}>
      <div style={styles.row}>
        <button
          type="button"
          onClick={onHome}
          style={styles.iconBtn}
          aria-label="The Sentinel Room Home"
          title="Return to Home"
        >
          <img
            src="/img/sentinel-logo.svg"
            alt="The Sentinel Room"
            style={{ width: 24, height: "auto", display: "inline-block", verticalAlign: "middle" }}
          />
        </button>
        <div style={styles.searchSlot}>{searchSlot}</div>
        <div style={styles.rightSlot}>{right}</div>
      </div>
    </header>
  );
}

/**
 * Sync / online indicator — extracted from StickyHeader so callers can
 * place it elsewhere (e.g. just under the "Station · Travis" eyebrow line
 * instead of under the search bar). Driven by `lastSyncedAt`.
 */
export function SyncIndicator({ lastSyncedAt }: { lastSyncedAt?: number | null }) {
  const online = useOnlineStatus();
  const ago = useTimeAgo(lastSyncedAt ?? null);
  return (
    <div style={styles.statusBar} role="status" aria-live="polite">
      <span
        style={{
          ...styles.dot,
          background: online ? "#22c55e" : "#ef4444",
        }}
        aria-hidden
      />
      <span style={styles.statusText}>
        {online
          ? `Online · ${ago ? `synced ${ago}` : "syncing"}`
          : `Offline · ${ago ? `cached ${ago}` : "cached recipes"}`}
      </span>
    </div>
  );
}

function useTimeAgo(epochMs: number | null): string | null {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!epochMs) return;
    const t = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(t);
  }, [epochMs]);
  if (!epochMs) return null;
  const sec = Math.max(0, Math.floor((now - epochMs) / 1000));
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    // Keep stable references so removeEventListener matches what was registered.
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);
  return online;
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "var(--bg)",
    paddingTop: 8,
    paddingBottom: 6,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "40px 1fr auto",
    gap: 6,
    alignItems: "center",
  },
  iconBtn: {
    height: 40,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
  },
  searchSlot: { minWidth: 0 },
  rightSlot: { display: "flex", alignItems: "center", gap: 6 },
  statusBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
    paddingLeft: 4,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    display: "inline-block",
  },
  statusText: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
};
